import { useTranslation } from 'react-i18next';

import {
  FullScreenLoader,
  IdentityWizardLayout,
  LinkErrorScreen,
  nonLinkErrorCode,
} from '@identity/presentation/components/base';
import type { LinkErrorScreenCode, LinkErrorScreenCopy } from '@identity/presentation/components/base';
import { EmailStep, PasswordStep, PhoneStep } from '@identity/presentation/components/specific/steps';
import { VERIFY_ACCOUNT_STEP_ORDER } from '@identity/presentation/constants';
import { useAccountStatus, useSessionExpiredExit } from '@identity/presentation/hooks';
import { nextStepFor, unsupportedRequirements } from '@identity/presentation/utils';

import { VerificationDoneScreen } from './VerificationDoneScreen';

/**
 * Controls the account verification flow: it reads the account status, derives the current step and
 * renders it. Like the invitation wizard, the step is derived on every render and never stored.
 *
 * Two things set it apart. It can render the email step, which is its first one, so an unknown key
 * is its only source of a block; and it shows no progress, so it needs no step list.
 *
 * Skipping a requirement matters more here than in the other flow: it would also switch off the gate
 * of an account the policy still considers unsafe.
 *
 * It logs nothing. Every call is already reported by its use case.
 */
export const AccountVerificationWizard = () => {
  const { t } = useTranslation('identity');
  const { data, error, isLoading, refetch } = useAccountStatus();

  useSessionExpiredExit(error);

  const requirements = data?.requirements;
  const currentStep = requirements ? nextStepFor(VERIFY_ACCOUNT_STEP_ORDER, requirements) : undefined;
  const blockingRequirement = requirements
    ? unsupportedRequirements(VERIFY_ACCOUNT_STEP_ORDER, requirements)[0]
    : undefined;

  /**
   * The copy of the error screen of this flow. It never mentions the link, because there is none
   * here, and never names the unknown requirement, which belongs in the log and not on a screen.
   */
  const errorCopy: LinkErrorScreenCopy = {
    body: t('verifyAccount.error.body'),
    retryLabel: t('verifyAccount.error.action'),
    title: t('verifyAccount.error.title'),
  };

  const renderError = (code: LinkErrorScreenCode, copy?: LinkErrorScreenCopy) => (
    <LinkErrorScreen code={code} copy={copy} onRetry={() => refetch()} />
  );

  // A dead session already left for the sign in screen above, so what is left here is a failure of
  // ours or of the connection. A lost connection keeps the default copy: the words of this flow
  // blame our side, which is true of every other failure and not of that one.
  if (error) {
    const code = nonLinkErrorCode(error);

    return renderError(code, code === 'NETWORK' ? undefined : errorCopy);
  }

  if (isLoading || !data || !requirements || !currentStep) return <FullScreenLoader />;

  // Stop, do not skip: finishing by ignoring a requirement would switch off the gate of an account
  // the policy still considers unsafe.
  if (blockingRequirement) return renderError('LINK_INVALID', errorCopy);

  return (
    <IdentityWizardLayout>
      {currentStep === 'EMAIL' && <EmailStep accountEmail={data.account.email} />}
      {currentStep === 'PASSWORD' && <PasswordStep />}
      {currentStep === 'PHONE' && <PhoneStep />}
      {currentStep === 'DONE' && <VerificationDoneScreen />}
    </IdentityWizardLayout>
  );
};
